// "Go to this brand" channel between navigation UI (sidebar list, header
// brands menu) and the /brand carousel.
//
// Router state alone only works on a FRESH arrival: the carousel mounts, its
// rows load, and its effect reads state.targetBrand once. Once the carousel is
// already mounted on /brand there is nowhere new to navigate to — a same-route
// navigate() carries new state, but nothing guarantees the mounted carousel
// acts on it, and a second pick could quietly go nowhere. This channel hands
// the request straight to the mounted carousel, and parks it for the mount
// when the carousel is not there yet. Both callers use it; state.targetBrand
// remains for other arrivals (e.g. a product page's brand link).
const EVENT = "brand-nav-request";
let pending = null;

/** Ask the brand page to land on `name` (lowercased brand name). With
 *  `{ jump: true }` a mounted carousel lands in place instead of gliding
 *  there: the showcase list over the page shows each name it is pointed at
 *  on the page behind (SearchModal), and a glide per name would fly the deck
 *  through every brand in between. A parked request lands in place anyway. */
export function requestBrandNav(name, { jump = false } = {}) {
  const n = String(name || "").toLowerCase().trim();
  if (!n) return;
  pending = n;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { name: n, jump } }));
  }
}

/** The carousel calls this once its rows exist: a request made before it
 *  mounted is returned (and cleared); otherwise null. */
export function takePendingBrandNav() {
  const n = pending;
  pending = null;
  return n;
}

/** Whether a request is waiting for the carousel, without taking it. The
 *  brands page reads this at mount to skip its index when a brand was already
 *  chosen elsewhere; the carousel still takes the request itself. */
export function peekPendingBrandNav() {
  return pending;
}

/** Subscribe the mounted carousel: handler(name, { jump }). Returns the
 *  unsubscribe. */
export function onBrandNav(handler) {
  if (typeof window === "undefined") return () => {};
  const fn = (e) => handler(e.detail && e.detail.name, { jump: Boolean(e.detail && e.detail.jump) });
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}
